<template>
  <section v-if="groups.length" class="site-links" :class="{ inset }" aria-labelledby="site-links-title" data-site-links>
    <h2 id="site-links-title" class="site-links-title">{{ zh ? '相關' : 'Connected' }}</h2>
    <div v-for="g in groups" :key="g.id" class="site-links-group" :data-group="g.id">
      <h3>{{ g.label }}</h3>
      <ul>
        <li v-for="it in g.items" :key="it.key">
          <router-link :to="it.to">
            <span class="tag" :class="'k-' + it.kind">{{ it.kindLabel }}</span>
            <span class="site-links-name" :lang="it.lang">{{ it.title }}</span>
            <span v-if="it.meta" class="site-links-meta">{{ it.meta }}</span>
          </router-link>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup>
// 每個詳細頁底部的「相關」區（2026-10-04）：資料全部來自 scripts/build-interlinks.mjs 產的 src/data/interlinks.json，
// 這裡只負責顯示，不做任何比對。規則與上限見 docs/DESIGN.md §二十四。
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { withSlash } from '@/runtime/site-path.mjs'
import data from '@/data/interlinks.json'
import { KIND_ITEM } from '@/search/kinds.mjs'

const props = defineProps({ pageKey: { type: String, default: '' }, inset: Boolean })
const route = useRoute()
const zh = computed(() => route.path.startsWith('/zh/') || route.path === '/zh')
const key = computed(() => props.pageKey || withSlash((route.path.replace(/^\/zh(?=\/|$)/, '') || '/')))

const GROUP_LABEL = {
  zh: { in: '出現在', mentions: '提到的作品', grew: '長成的作品', year: '同一年', series: '同一條線', near: '語意相近' },
  en: { in: 'Seen in', mentions: 'Works mentioned', grew: 'Grew into', year: 'The same year', series: 'Along the same thread', near: 'Close in meaning' },
}
const KIND_LABEL = {
  zh: { ...KIND_ITEM.zh, strata: '地層', edition: 'NFT 資料庫', td: 'TD 每日創作', sketch: '程式草圖', lab: '實驗案件' },
  en: { ...KIND_ITEM.en, strata: 'Strata', edition: 'NFT records', td: 'TouchDesigner', sketch: 'p5.js sketch', lab: 'Lab record' },
}
const zhOnly = new Set(data.zhOnly || [])
const KIND_COLOR = { strata: 'lab' }

const groups = computed(() => {
  const rows = data.pages[key.value] || []
  const loc = zh.value ? 'zh' : 'en'
  const isStrata = /^\/strata\/\d{4}\/$/.test(key.value)
  return rows.map(([g, ...ids]) => ({
    id: g,
    label: isStrata && g === 'year' ? (zh.value ? '這一年' : 'This year') : GROUP_LABEL[loc][g],
    items: ids.map(i => {
      const [k, kind, nzh, nen, year, sub] = data.nodes[i]
      const showEn = !zh.value && nen
      const kk = KIND_COLOR[kind] || kind
      const [pathPart, hash] = k.split('#')
      return {
        key: k, kind: kk, kindLabel: KIND_LABEL[loc][kind] || kind,
        title: showEn ? nen : nzh, lang: showEn ? 'en' : 'zh-Hant',
        meta: [kind === 'strata' ? '' : year, sub].filter(Boolean).join('　'),
        to: { path: withSlash((zh.value || zhOnly.has(i) ? '/zh' : '') + pathPart), hash: hash ? '#' + hash : '' },
      }
    }),
  })).filter(g => g.items.length)
})
</script>

<style scoped>
.site-links { max-width: var(--editorial-w, 1560px); margin: 112px auto 0; padding: 0 max(24px, 5vw); text-align: left; color: var(--ink, #f4f4f2); }
.site-links.inset { padding: 0; margin-top: 72px; max-width: none; }
.site-links-title { font-family: var(--font-display, inherit); font-size: 1.6rem; font-weight: 500; margin: 0 0 28px; }
.site-links-group { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 8px 32px; padding: 22px 0; border-top: 1px solid var(--line-2, rgba(255,255,255,.1)); }
.site-links-group h3 { font-size: 0.78rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3, #8b8b86); font-weight: 600; margin: 4px 0 0; }
.site-links-group ul { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr)); gap: 2px 24px; }
.site-links-group a { display: grid; grid-template-columns: 7.4em 1fr; align-items: baseline; gap: 2px 14px; min-height: 44px; padding: 9px 12px; margin: 0 -12px; border-radius: var(--r-sm, 6px); text-decoration: none; color: inherit; }
.site-links-group a:hover { background: var(--tint-2, rgba(255,255,255,.06)); }
.site-links-group a:hover .site-links-name { color: var(--gold, #d9c267); }
.site-links-group a:focus-visible { outline: 2px solid var(--gold, #d9c267); outline-offset: 2px; }
.site-links .tag { min-width: 0; }
.site-links-name { font-size: 1rem; line-height: 1.5; color: var(--ink, #f4f4f2); overflow-wrap: anywhere; }
.site-links-meta { grid-column: 2; font-size: 0.78rem; color: var(--ink-3, #8b8b86); line-height: 1.4; }
@media (max-width: 991px) {
  .site-links { margin-top: 80px; }
  .site-links-group { grid-template-columns: 1fr; gap: 6px; }
}
@media (max-width: 575px) {
  .site-links .tag { min-width: 0; }
  .site-links-group a { grid-template-columns: 1fr; gap: 2px; }
  .site-links-meta { grid-column: 1; }
}
</style>
